/* global axios */
<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import Avatar from 'next/avatar/Avatar.vue';

const router = useRouter();
const { accountId } = useAccount();

const agents = ref([]);
const search = ref('');
const statusFilter = ref('all');
const isLoading = ref(true);
const errorMessage = ref('');
let refreshTimer;

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'online', label: 'Online' },
  { value: 'busy', label: 'Busy' },
  { value: 'offline', label: 'Offline' },
];

const statusCounts = computed(() => ({
  all: agents.value.length,
  online: agents.value.filter(
    agent => agent.availability_status === 'online'
  ).length,
  busy: agents.value.filter(
    agent => agent.availability_status === 'busy'
  ).length,
  offline: agents.value.filter(
    agent => agent.availability_status === 'offline'
  ).length,
}));

const filteredAgents = computed(() => {
  const query = search.value.trim().toLocaleLowerCase();

  return agents.value.filter(agent => {
    const matchesStatus =
      statusFilter.value === 'all' ||
      agent.availability_status === statusFilter.value;

    const searchable = [
      agent.name,
      agent.email,
      ...(agent.teams || []).map(team => team.name),
    ]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase();

    return matchesStatus && (!query || searchable.includes(query));
  });
});

const statusClass = status => {
  if (status === 'online') {
    return 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300';
  }

  if (status === 'busy') {
    return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300';
  }

  return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
};

const fetchAgents = async () => {
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/agent_statuses`
    );

    agents.value = response.data;
    errorMessage.value = '';
  } catch (error) {
    errorMessage.value = 'Agent status list could not be loaded.';
  } finally {
    isLoading.value = false;
  }
};

const openAgentConversations = agent => {
  router.push({
    name: 'agent_conversations',
    params: {
      accountId: accountId.value,
      agentId: agent.id,
    },
  });
};

onMounted(() => {
  fetchAgents();
  refreshTimer = window.setInterval(fetchAgents, 30000);
});

onBeforeUnmount(() => {
  window.clearInterval(refreshTimer);
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-background">
    <header class="px-6 py-5 border-b border-n-weak">
      <h1 class="text-xl font-semibold text-n-slate-12">
        Agent Status
      </h1>
      <p class="mt-1 text-sm text-n-slate-11">
        View agent availability and assigned conversations.
      </p>
    </header>

    <div class="p-6">
      <div class="grid grid-cols-2 gap-3 mb-5 md:grid-cols-4">
        <button
          v-for="option in statusOptions"
          :key="option.value"
          type="button"
          class="p-4 text-left border rounded-xl border-n-weak bg-n-alpha-2 hover:bg-n-alpha-3"
          :class="{
            'outline outline-2 outline-n-brand':
              statusFilter === option.value,
          }"
          @click="statusFilter = option.value"
        >
          <div class="text-xs uppercase text-n-slate-10">
            {{ option.label }}
          </div>
          <div class="mt-1 text-2xl font-semibold text-n-slate-12">
            {{ statusCounts[option.value] }}
          </div>
        </button>
      </div>

      <div class="mb-4">
        <input
          v-model="search"
          type="search"
          placeholder="Search agent, email or team"
          class="w-full max-w-lg h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
        />
      </div>

      <div
        v-if="errorMessage"
        class="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg"
      >
        {{ errorMessage }}
      </div>

      <div
        v-if="isLoading"
        class="p-8 text-center text-n-slate-11"
      >
        Loading agent status…
      </div>

      <div
        v-else
        class="overflow-hidden border rounded-xl border-n-weak"
      >
        <table class="w-full">
          <thead class="bg-n-alpha-2">
            <tr class="text-left text-xs uppercase text-n-slate-10">
              <th class="px-4 py-3">Agent</th>
              <th class="px-4 py-3">Status</th>
              <th class="px-4 py-3">Team</th>
              <th class="px-4 py-3 text-center">Open</th>
              <th class="px-4 py-3 text-center">Unattended</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="agent in filteredAgents"
              :key="agent.id"
              class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-2"
              @click="openAgentConversations(agent)"
            >
              <td class="px-4 py-3">
                <div class="flex items-center gap-3">
                  <Avatar
                    :src="agent.thumbnail"
                    :name="agent.name"
                    :status="agent.availability_status"
                    :size="36"
                    rounded-full
                  />
                  <div>
                    <div class="text-sm font-medium text-n-slate-12">
                      {{ agent.name }}
                    </div>
                    <div class="text-xs text-n-slate-10">
                      {{ agent.email }}
                    </div>
                  </div>
                </div>
              </td>

              <td class="px-4 py-3">
                <span
                  class="inline-flex px-2 py-1 text-xs capitalize rounded-full"
                  :class="statusClass(agent.availability_status)"
                >
                  {{ agent.availability_status }}
                </span>
              </td>

              <td class="px-4 py-3 text-sm text-n-slate-11">
                {{
                  (agent.teams || [])
                    .map(team => team.name)
                    .join(', ') || '—'
                }}
              </td>

              <td class="px-4 py-3 text-sm text-center text-n-slate-12">
                {{ agent.open_conversations }}
              </td>

              <td class="px-4 py-3 text-sm text-center text-n-slate-12">
                {{ agent.unattended_conversations }}
              </td>
            </tr>

            <tr v-if="!filteredAgents.length">
              <td
                colspan="5"
                class="px-4 py-10 text-sm text-center text-n-slate-10"
              >
                No agents found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>
